import { useState, memo, useMemo, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { formatInCentralTime } from "@/utils/timezone";
import { ViewToggle } from "@/components/events/ViewToggle";
import { EventCalendar } from "@/components/events/EventCalendar";
import { Calendar, ArrowRight } from "lucide-react";
import { toast } from "sonner";

interface ScheduleSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface ScheduleItem {
  id: string;
  time: string;
  title: string;
  location: string;
  color: string;
}

// Memoized schedule event card
const ScheduleEventCard = memo(function ScheduleEventCard({ 
  item, 
  onClick 
}: { 
  item: ScheduleItem; 
  onClick: () => void;
}) {
  return (
    <div
      className="bg-card rounded-xl p-4 card-shadow tap-scale flex gap-4 cursor-pointer border"
      onClick={onClick}
    >
      <div className={`w-1 rounded-full ${item.color}`} />
      <div className="flex-1">
        <div className="text-sm text-muted-foreground">{item.time}</div>
        <div className="text-base font-bold text-foreground mt-1">
          {item.title}
        </div>
        <div className="text-sm text-muted-foreground mt-1">
          {item.location}
        </div>
      </div>
      <ArrowRight className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-2" />
    </div>
  );
});

export const ScheduleSheet = memo(function ScheduleSheet({
  open,
  onOpenChange,
}: ScheduleSheetProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [view, setView] = useState<'list' | 'calendar'>('list');

  const { data: rsvpEvents = [], isLoading } = useQuery({
    queryKey: ['schedule-sheet', user?.id],
    queryFn: async () => {
      if (!user) return [];
      
      const now = new Date();
      
      const { data, error } = await supabase
        .from('event_attendances')
        .select(`
          *,
          events (*)
        `)
        .eq('user_id', user.id)
        .eq('status', 'rsvp')
        .gte('events.date', now.toISOString())
        .order('events(date)', { ascending: true });
      
      if (error) throw error;
      
      // Filter out any null events (should not happen but safety check)
      return data?.filter(item => item.events) || [];
    },
    enabled: !!user && open,
  });

  // Memoize extracted events for calendar
  const events = useMemo(() => 
    rsvpEvents.map(rsvp => rsvp.events).filter(Boolean),
    [rsvpEvents]
  );

  // Memoize user RSVP IDs for calendar
  const userRsvpIds = useMemo(() => 
    rsvpEvents.map(r => r.event_id),
    [rsvpEvents]
  );

  // Memoize grouped events for list view (using Central Time)
  const groupedEvents = useMemo(() => 
    rsvpEvents.reduce((acc: Record<string, ScheduleItem[]>, rsvp: any) => {
      const event = rsvp.events;
      if (!event) return acc;
      
      const date = formatInCentralTime(event.date, 'EEEE, MMM d');
      
      if (!acc[date]) acc[date] = [];
      acc[date].push({
        id: event.id,
        time: formatInCentralTime(event.date, 'h:mm a'),
        title: event.title,
        location: event.location,
        color: 'bg-primary',
      });
      
      return acc;
    }, {}),
    [rsvpEvents]
  );

  // Handle RSVP cancellation
  const handleRsvp = useCallback(async (eventId: string) => {
    if (!user) return;

    try {
      await supabase
        .from('event_attendances')
        .delete()
        .eq('event_id', eventId)
        .eq('user_id', user.id);
      
      toast.success("RSVP cancelled");
      queryClient.invalidateQueries({ queryKey: ['schedule-sheet'] });
      queryClient.invalidateQueries({ queryKey: ['schedule'] });
      queryClient.invalidateQueries({ queryKey: ['my-events-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['my-events-schedule'] });
      queryClient.invalidateQueries({ queryKey: ['today-checkin-events'] });
      queryClient.invalidateQueries({ queryKey: ['my-rsvps'] });
      queryClient.invalidateQueries({ queryKey: ['user-rsvp-count'] });
      queryClient.invalidateQueries({ queryKey: ['event-attendance'] });
      queryClient.invalidateQueries({ queryKey: ['event-attendees'] });
    } catch (error: any) {
      toast.error(error.message);
    }
  }, [user, queryClient]);

  const handleEventClick = useCallback((eventId: string) => {
    onOpenChange(false);
    navigate(`/events/${eventId}`);
  }, [navigate, onOpenChange]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85vh] rounded-t-xl">
        <SheetHeader className="pb-4">
          <SheetTitle className="flex items-center justify-between">
            <span>Your Schedule</span>
            <span className="text-sm font-normal text-muted-foreground">
              {rsvpEvents.length} upcoming event{rsvpEvents.length !== 1 ? 's' : ''}
            </span>
          </SheetTitle>
        </SheetHeader>
        
        {/* View Toggle */}
        <div className="pb-4">
          <ViewToggle view={view} onViewChange={setView} />
        </div>
        
        <ScrollArea className="h-[calc(85vh-160px)]">
          {isLoading ? (
            <div className="space-y-3 pr-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full rounded-xl" />
              ))}
            </div>
          ) : view === 'calendar' ? (
            <div className="pr-4">
              <EventCalendar
                events={events}
                userRsvps={userRsvpIds}
                onRsvp={handleRsvp}
              />
            </div>
          ) : Object.keys(groupedEvents).length > 0 ? (
            <div className="space-y-6 pr-4">
              {Object.entries(groupedEvents).map(([date, items]) => (
                <div key={date}>
                  <h2 className="text-sm font-semibold text-muted-foreground mb-3 uppercase tracking-wide">
                    {date}
                  </h2>
                  <div className="space-y-3">
                    {items.map((item) => (
                      <ScheduleEventCard
                        key={item.id}
                        item={item}
                        onClick={() => handleEventClick(item.id)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
              <Calendar className="w-16 h-16 mb-4 text-muted-foreground" />
              <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                No upcoming events
              </h3>
              <p className="text-muted-foreground mb-6">
                RSVP to events to start building your schedule
              </p>
              <Button 
                onClick={() => {
                  onOpenChange(false);
                  navigate('/events');
                }}
              >
                Browse Events
              </Button>
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
});
